import { useState } from 'react'

import { Box, Button, PopoverMenu, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessAgreementDocumentCardEsignaturesFragment,
  PerformanceProcessAgreementDocumentCardFragment,
} from 'generated/resources/graphql'

import DocumentType from 'modules/core/components/DocumentType'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import DocumentModal from 'modules/documents/components/DocumentModal'
import SignatureStatusTag from 'modules/documents/components/Documents/List/Item/SignatureStatusTag'

const getSignaturesCountLabel = (
  document: PerformanceProcessAgreementDocumentCardFragment,
  esignatures: PerformanceProcessAgreementDocumentCardEsignaturesFragment[]
) => {
  if (!document.signatureStatus) {
    return i18n.t('performance_process.agreements.agreement_list_card.confirmations', {
      count: 2,
      total: 2,
    })
  }

  if (esignatures.length === 0) return

  return i18n.t('performance_process.agreements.agreement_list_card.signatures', {
    count: esignatures.filter((signature) => signature.status === 'signed').length,
    total: esignatures.length,
  })
}

interface Props {
  document: PerformanceProcessAgreementDocumentCardFragment
  esignatures: PerformanceProcessAgreementDocumentCardEsignaturesFragment[]
  esignaturesLoading: boolean
}

export const DocumentCard = ({ document, esignatures, esignaturesLoading }: Props) => {
  const [openPreview, setOpenPreview] = useState(false)

  if (!document) return null

  const signaturesCountLabel = getSignaturesCountLabel(document, esignatures)

  return (
    <Box
      width="full"
      flexDirection="row"
      padding="s16"
      alignItems="center"
      justifyContent="spaceBetween"
      border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
      borderRadius={{ all: 'abs008' }}
    >
      <Box flexDirection="row" alignItems="center" gap="s16">
        <DocumentType mimetype="pdf" />

        <Box gap="s4">
          <Text.Normal weight="medium" ellipsis>
            {i18n.t('performance_process.agreements.page.title')}
          </Text.Normal>

          {esignaturesLoading ? (
            <Skeleton width="s200" height="s20" />
          ) : (
            <Text.Normal size="200" color="grey800" ellipsis>
              {signaturesCountLabel}
              {signaturesCountLabel && ' · '}
              {i18n.t('documents.employee.list.created_at')}{' '}
              {i18nDateToString(stringToDate(document.createdAt, 'backend_datetime'), 'ui')}
            </Text.Normal>
          )}
        </Box>
      </Box>
      <Box flexDirection="row" alignItems="center" gap="s8" paddingLeft="s8">
        <SignatureStatusTag status={document.signatureStatus ?? 'completed'} />

        {document.file && (
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="end">
              <PopoverMenu.HrefOption
                href={buildUrl(document.file)}
                title={i18n.t('documents.download')}
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        )}

        <Button
          type="secondary"
          aria-label={i18n.t('common.view_document')}
          onClick={() => setOpenPreview(true)}
          iconName="arrow-right"
        />
      </Box>

      {openPreview && (
        <DocumentModal
          documentId={document.id.toString()}
          documents={[]}
          onClose={() => setOpenPreview(false)}
        />
      )}
    </Box>
  )
}
